import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

// Height of the phone status bar (time, battery, network icons).
// It is 0 on a laptop, so nothing changes there.
const SAFE_TOP = "var(--safe-area-inset-top, env(safe-area-inset-top, 0px))";

const NAVBAR_COLOR = "#a9bfcc";
const NAVY = "#292d78";
const TEAL = "#23868a";

/* ---------- Small icons ---------- */

const MenuIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M4 7h16M4 12h11M4 17h16" />
  </svg>
);

const PowerIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3v9" />
    <path d="M6.4 6.6a8 8 0 1 0 11.2 0" />
  </svg>
);

// "S" monogram for Softvolute
const SMark = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17 7.5c-1-1.6-2.8-2.5-5-2.5-2.7 0-4.5 1.4-4.5 3.3 0 4.8 9.5 2.4 9.5 7.4 0 2-2 3.3-5 3.3-2.3 0-4.2-.9-5.2-2.6" />
  </svg>
);

const Navbar = ({
  onLogout,
  isMobile,
  toggleSidebar,
  showMenuButton = true,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Tapping the logo or the company name goes to the Home page
  const goHome = () => navigate("/");
  const onHomeKey = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      goHome();
    }
  };

  // Get logged-in user
  let user = {};

  try {
    user = JSON.parse(localStorage.getItem("user") || "{}");
  } catch (error) {
    user = {};
  }

  const userName = user?.name || user?.fullName || user?.username || "User";

  const rawRole =
    user?.role || user?.roleName || localStorage.getItem("userRole") || "";

  // Convert role to readable format
  const formatRole = (role) => {
    if (!role) return "";

    return role
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const roleName = formatRole(rawRole);
  const initial = String(userName).trim().charAt(0).toUpperCase() || "U";

  /* ---------- Reusable pieces ---------- */

  const avatarStyle = (size) => ({
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: "50%",
    backgroundColor: NAVY,
    color: "#ffffff",
    fontSize: `${Math.round(size * 0.45)}px`,
    fontWeight: "800",
    boxShadow: "0 0 0 3px rgba(255,255,255,0.55)",
  });

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "10px",
        width: "100%",
        // 70px bar + the phone status-bar height on top (0 on a laptop)
        height: `calc(70px + ${SAFE_TOP})`,
        padding: isMobile ? `${SAFE_TOP} 12px 3px` : `${SAFE_TOP} 24px 3px`,
        backgroundColor: NAVBAR_COLOR,
        // soft light sheen on the same colour
        backgroundImage:
          "linear-gradient(180deg, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0) 100%)",
        boxSizing: "border-box",
        boxShadow: "0 6px 18px rgba(41,45,120,0.16)",

        position: "fixed",
        top: 0,
        left: 0,

        zIndex: 1000,
      }}
    >
      {/* Teal to navy accent line along the bottom edge */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: "3px",
          backgroundImage: `linear-gradient(90deg, ${TEAL}, ${NAVY})`,
          pointerEvents: "none",
        }}
      />

      {/* ================= LEFT SIDE ================= */}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? "10px" : "14px",
          minWidth: 0,
        }}
      >
        {/* Mobile Sidebar Button */}
        {isMobile && showMenuButton && (
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label="Open menu"
            style={{
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              width: "44px",
              height: "44px",
              border: "1px solid rgba(41,45,120,0.18)",
              borderRadius: "14px",
              backgroundColor: "rgba(255,255,255,0.45)",
              color: NAVY,
              cursor: "pointer",
              padding: 0,
            }}
          >
            <MenuIcon />
          </button>
        )}

        {/* Logo mark */}
        <div
          onClick={goHome}
          onKeyDown={onHomeKey}
          role="link"
          tabIndex={0}
          title="Home"
          style={{
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            width: isMobile ? "40px" : "46px",
            height: isMobile ? "40px" : "46px",
            borderRadius: "14px",
            backgroundImage: `linear-gradient(135deg, ${TEAL}, ${NAVY})`,
            color: "#ffffff",
            boxShadow: "0 6px 14px rgba(41,45,120,0.28)",
          }}
        >
          <SMark />
        </div>

        {/* Two-line company name */}
        <div
          onClick={goHome}
          onKeyDown={onHomeKey}
          role="link"
          tabIndex={0}
          title="Home"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "3px",
            minWidth: 0,
            cursor: "pointer",
          }}
        >
          <span
            style={{
              fontSize: isMobile ? "16px" : "20px",
              fontWeight: "800",
              lineHeight: 1,
              color: NAVY,
              letterSpacing: "-0.3px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Softvolute
          </span>

          <span
            style={{
              fontSize: isMobile ? "8.5px" : "10px",
              fontWeight: "800",
              lineHeight: 1,
              color: TEAL,
              letterSpacing: "2.2px",
              whiteSpace: "nowrap",
            }}
          >
            BUILDCON LLP
          </span>
        </div>
      </div>

      {/* ================= DESKTOP RIGHT SIDE ================= */}

      {!isMobile && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
          }}
        >
          {/* User chip: avatar + name + role */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "6px 18px 6px 6px",
              backgroundColor: "rgba(255,255,255,0.4)",
              border: "1px solid rgba(255,255,255,0.65)",
              borderRadius: "999px",
            }}
          >
            <div style={avatarStyle(40)}>{initial}</div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: "700",
                  lineHeight: 1,
                  color: NAVY,
                  opacity: 0.75,
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                Welcome back
              </span>

              <span
                style={{
                  fontSize: "15px",
                  fontWeight: "800",
                  lineHeight: 1,
                  color: NAVY,
                  whiteSpace: "nowrap",
                }}
              >
                {userName}
              </span>
            </div>

            {roleName && (
              <span
                style={{
                  marginLeft: "4px",
                  padding: "7px 12px",
                  backgroundColor: NAVY,
                  color: "#ffffff",
                  borderRadius: "999px",
                  fontSize: "11px",
                  fontWeight: "700",
                  letterSpacing: "0.3px",
                  whiteSpace: "nowrap",
                }}
              >
                {roleName}
              </span>
            )}
          </div>

          {/* Logout */}

          <button
            type="button"
            onClick={onLogout}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "9px",
              height: "46px",
              padding: "0 22px",
              backgroundColor: TEAL,
              color: "#ffffff",
              border: "none",
              borderRadius: "999px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "700",
              boxShadow: "0 8px 18px rgba(35,134,138,0.35)",
            }}
          >
            <PowerIcon />
            Logout
          </button>
        </div>
      )}

      {/* ================= MOBILE RIGHT SIDE ================= */}

      {isMobile && (
        <div style={{ flexShrink: 0 }}>
          {/* Avatar button opens the account card */}
          <button
            type="button"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            aria-label="Account menu"
            aria-expanded={isMenuOpen}
            style={{
              display: "grid",
              placeItems: "center",
              width: "44px",
              height: "44px",
              padding: 0,
              background: "transparent",
              border: "none",
              cursor: "pointer",
            }}
          >
            <span style={avatarStyle(38)}>{initial}</span>
          </button>

          {isMenuOpen && (
            <>
              {/* Tap anywhere outside the card to close it */}
              <button
                type="button"
                onClick={() => setIsMenuOpen(false)}
                aria-label="Close menu"
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 1001,
                  width: "100%",
                  height: "100%",
                  padding: 0,
                  background: "transparent",
                  border: "none",
                  cursor: "default",
                }}
              />

              <div
                style={{
                  position: "absolute",
                  right: "12px",
                  top: `calc(64px + ${SAFE_TOP})`,
                  zIndex: 1002,
                  width: "230px",
                  padding: "16px",
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  boxShadow: "0 12px 30px rgba(41,45,120,0.28)",
                  boxSizing: "border-box",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    paddingBottom: "14px",
                    marginBottom: "14px",
                    borderBottom: "1px solid #e5eaf0",
                  }}
                >
                  <div style={avatarStyle(44)}>{initial}</div>

                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "800",
                        color: "#1f2340",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {userName}
                    </div>

                    {roleName && (
                      <div
                        style={{
                          display: "inline-block",
                          marginTop: "5px",
                          padding: "3px 9px",
                          backgroundColor: NAVY,
                          color: "#ffffff",
                          borderRadius: "999px",
                          fontSize: "10.5px",
                          fontWeight: "700",
                        }}
                      >
                        {roleName}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    onLogout();
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "9px",
                    width: "100%",
                    minHeight: "46px",
                    border: "none",
                    backgroundColor: TEAL,
                    color: "#ffffff",
                    padding: "10px 12px",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontSize: "15px",
                    fontWeight: "700",
                  }}
                >
                  <PowerIcon />
                  Logout
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Navbar;
